import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, BookOpen, CheckCircle2 } from "lucide-react";

const guides = {
  "ai-rang-sanya-kue-arai": {
    title: "AI ร่างสัญญาคืออะไร",
    description: "อธิบายว่า AI ร่างสัญญาทำงานอย่างไร เหมาะกับงานกฎหมายแบบไหน และควรใช้ร่วมกับการตรวจของนักกฎหมายอย่างไร",
    keywords: "AI ร่างสัญญาคืออะไร, AI ร่างสัญญา, legal drafting AI, ร่างสัญญาด้วย AI",
    intro: "AI ร่างสัญญาคือเครื่องมือที่ช่วยเปลี่ยน brief หรือข้อมูลตั้งต้นให้เป็นร่างเอกสารกฎหมายที่มีโครงสร้าง ตรวจต่อได้ และปรับตามบริบทของคู่สัญญา",
    sections: [
      ["AI ร่างสัญญาช่วยอะไร", "ช่วยเริ่มจากหน้าว่าง จัดหัวข้อสำคัญ สร้าง clause ตั้งต้น ปรับภาษากฎหมาย และชี้จุดที่ควรตรวจเพิ่มเติมก่อนใช้งานจริง"],
      ["เหมาะกับงานแบบไหน", "เหมาะกับสัญญาจ้างบริการ สัญญาเช่า สัญญาซื้อขาย NDA หนังสือบอกกล่าว และเอกสารที่มีโครงสร้างซ้ำแต่ต้องปรับตามบริบท"],
      ["ข้อควรระวัง", "AI ไม่ควรแทนการวินิจฉัยของทนายหรือผู้เชี่ยวชาญ เอกสารที่มีผลทางกฎหมายควรถูกตรวจทานก่อนส่งหรือลงนามเสมอ"],
    ],
    checklist: ["เตรียม brief ให้ชัด", "ระบุฝ่ายที่ต้องการปกป้อง", "ตรวจ clause สำคัญ", "ตรวจข้อกฎหมายที่เกี่ยวข้อง", "ให้ผู้เชี่ยวชาญ review final"],
    related: ["/contract-drafting-ai", "/contract-review-ai", "/mcp"],
  },
  "withi-truat-sanya-kon-sen": {
    title: "วิธีตรวจสัญญาก่อนเซ็น",
    description: "เช็กลิสต์การตรวจสัญญาก่อนลงนาม ทั้งขอบเขตงาน การชำระเงิน ความรับผิด การเลิกสัญญา และข้อกฎหมายที่ควรระวัง",
    keywords: "วิธีตรวจสัญญาก่อนเซ็น, ตรวจสัญญา, AI ตรวจสัญญา, redline สัญญา",
    intro: "ก่อนเซ็นสัญญา ควรตรวจทั้งเนื้อหาทางธุรกิจและผลทางกฎหมาย เพราะ clause สั้น ๆ บางข้ออาจสร้างภาระผูกพันระยะยาวได้",
    sections: [
      ["ตรวจคู่สัญญาและอำนาจลงนาม", "ตรวจชื่อ เลขทะเบียน ที่อยู่ และอำนาจของผู้ลงนามให้ตรงกับเอกสารประกอบ"],
      ["ตรวจขอบเขตงานและเงิน", "ขอบเขตงาน deliverable วันส่งมอบ วิธีชำระเงิน เบี้ยปรับ และภาษีควรระบุให้ชัด"],
      ["ตรวจความรับผิดและการเลิกสัญญา", "ดู limitation of liability, indemnity, event of default, notice period และผลหลังเลิกสัญญา"],
    ],
    checklist: ["คู่สัญญาถูกต้อง", "ขอบเขตงานชัด", "payment term ชัด", "ความรับผิดไม่กว้างเกินไป", "มีวิธีเลิกสัญญาและ notice"],
    related: ["/contract-review-ai", "/contract-drafting-ai", "/thai-law-search"],
  },
  "kho-kuan-rawang-sanya-jang-borikan": {
    title: "ข้อควรระวังในสัญญาจ้างบริการ",
    description: "ประเด็นสำคัญที่ควรตรวจในสัญญาจ้างบริการ เช่น ขอบเขตงาน การส่งมอบ ความรับผิด ทรัพย์สินทางปัญญา และการเลิกสัญญา",
    keywords: "สัญญาจ้างบริการ, ตรวจสัญญาจ้างบริการ, ร่างสัญญาจ้างบริการ, ข้อควรระวังสัญญา",
    intro: "สัญญาจ้างบริการมักมีปัญหาจากขอบเขตงานไม่ชัด งานส่งมอบไม่ตรงความคาดหวัง หรือเงื่อนไขความรับผิดไม่สมดุล",
    sections: [
      ["ขอบเขตงานและ milestone", "ควรกำหนดสิ่งที่ต้องส่งมอบ เกณฑ์ตรวจรับ รอบแก้ไข และวันที่ครบกำหนดให้ชัด"],
      ["ค่าจ้างและค่าใช้จ่าย", "ระบุจำนวนเงิน รอบจ่าย ภาษี ค่าใช้จ่ายเพิ่มเติม และผลเมื่อจ่ายล่าช้า"],
      ["IP และข้อมูลลับ", "กำหนดว่าใครเป็นเจ้าของงาน ข้อมูลใดเป็นความลับ และใช้ผลงานต่อได้หรือไม่"],
    ],
    checklist: ["scope ชัด", "milestone ชัด", "รอบแก้ไขไม่เปิดกว้าง", "IP ownership ชัด", "termination clause ชัด"],
    related: ["/contract-drafting-ai", "/contract-review-ai", "/letter-of-demand-ai"],
  },
  "phit-sanya-pap": {
    title: "การผิดสัญญาตาม ป.พ.พ. ควรดูอะไร",
    description: "แนวคิดเบื้องต้นเกี่ยวกับการผิดสัญญา การบอกเลิกสัญญา ค่าเสียหาย และการใช้ข้อมูลกฎหมายประกอบการตรวจเอกสาร",
    keywords: "ผิดสัญญา ป.พ.พ., บอกเลิกสัญญา, ค่าเสียหายจากผิดสัญญา, กฎหมายแพ่งและพาณิชย์",
    intro: "เมื่อเกิดข้อพิพาทเรื่องสัญญา ควรดูทั้งข้อสัญญา ข้อเท็จจริง และบทกฎหมายที่เกี่ยวข้อง ไม่ใช่ดูเพียงข้อความในเอกสารอย่างเดียว",
    sections: [
      ["ผิดนัดหรือผิดสัญญาอย่างไร", "ต้องดูหน้าที่ตามสัญญา วันครบกำหนด การแจ้งเตือน และพฤติการณ์ของคู่สัญญา"],
      ["บอกเลิกสัญญาได้เมื่อไร", "หลายกรณีต้องมีการบอกกล่าวให้แก้ไขก่อน เว้นแต่สัญญาหรือกฎหมายกำหนดเป็นอย่างอื่น"],
      ["ค่าเสียหายต้องพิสูจน์", "ผู้เรียกค่าเสียหายควรมีหลักฐานความเสียหาย ความเชื่อมโยง และความเหมาะสมของจำนวนเงิน"],
    ],
    checklist: ["อ่าน clause ผิดนัด", "ตรวจ notice requirement", "เก็บหลักฐาน", "คำนวณความเสียหาย", "ตรวจมาตรากฎหมาย"],
    related: ["/mcp", "/thai-law-search", "/letter-of-demand-ai"],
  },
  "nangseu-bokklaw-kue-arai": {
    title: "หนังสือบอกกล่าวคืออะไร",
    description: "อธิบายหนังสือบอกกล่าว หนังสือทวงถาม และองค์ประกอบที่ควรมีเมื่อต้องแจ้งให้คู่สัญญาปฏิบัติหรือแก้ไขการผิดสัญญา",
    keywords: "หนังสือบอกกล่าวคืออะไร, หนังสือทวงถาม, demand letter, บอกกล่าวผิดสัญญา",
    intro: "หนังสือบอกกล่าวเป็นเอกสารที่ใช้แจ้งข้อเท็จจริง ข้อเรียกร้อง และกำหนดเวลาให้คู่สัญญาปฏิบัติก่อนดำเนินการขั้นต่อไป",
    sections: [
      ["ควรระบุอะไร", "ควรมีคู่สัญญา สัญญาที่เกี่ยวข้อง เหตุผิดสัญญา ข้อเรียกร้อง deadline และการสงวนสิทธิ"],
      ["น้ำเสียงสำคัญ", "ถ้อยคำควรชัด สุภาพ และไม่กล่าวเกินหลักฐาน เพราะหนังสืออาจถูกใช้เป็นหลักฐานในภายหลัง"],
      ["ใช้ AI ช่วยอย่างไร", "AI ช่วยจัดโครงสร้างและร่างถ้อยคำตั้งต้น แต่ควรตรวจข้อเท็จจริงและผลทางกฎหมายก่อนส่งจริง"],
    ],
    checklist: ["ข้อเท็จจริงถูกต้อง", "เรียกร้องชัด", "deadline ชัด", "แนบหลักฐาน", "ตรวจ tone และสิทธิทางกฎหมาย"],
    related: ["/letter-of-demand-ai", "/contract-review-ai", "/mcp"],
  },
  "nda-kuan-mee-arai-bang": {
    title: "NDA ควรมีอะไรบ้าง",
    description: "สรุปองค์ประกอบสำคัญของ NDA เช่น นิยามข้อมูลลับ วัตถุประสงค์ ข้อยกเว้น ระยะเวลา และการคืนข้อมูล",
    keywords: "NDA ควรมีอะไรบ้าง, ร่าง NDA, ตรวจ NDA, สัญญารักษาความลับ",
    intro: "NDA ที่ดีควรระบุขอบเขตข้อมูลลับและข้อยกเว้นอย่างสมดุล เพื่อคุ้มครองข้อมูลโดยไม่สร้างภาระที่กว้างเกินจำเป็น",
    sections: [
      ["นิยามข้อมูลลับ", "ระบุว่าข้อมูลประเภทใดถือเป็นความลับ และรวมถึงข้อมูลที่เปิดเผยด้วยวาจาหรือเอกสารหรือไม่"],
      ["วัตถุประสงค์และข้อจำกัด", "กำหนดว่ารับข้อมูลไปใช้เพื่ออะไร ใช้กับใครได้บ้าง และห้ามเปิดเผยต่อบุคคลใด"],
      ["ระยะเวลาและการคืนข้อมูล", "กำหนดระยะเวลารักษาความลับ วิธีคืนหรือทำลายข้อมูล และผลเมื่อผิดเงื่อนไข"],
    ],
    checklist: ["นิยามข้อมูลลับ", "ข้อยกเว้น", "purpose limitation", "ระยะเวลา", "return/destruction clause"],
    related: ["/nda-drafting-ai", "/contract-drafting-ai", "/privacy-policy-generator"],
  },
};

const guideSlugs = Object.keys(guides);
export { guideSlugs };

const navItems = [
  ["หน้าหลัก", "/"],
  ["KHORN Web App", "/product"],
  ["KHORN LINE", "/line"],
  ["KHORN MCP", "/mcp"],
  ["ราคา", "/#pricing"],
  ["ความปลอดภัย", "/privacy"],
  ["ติดต่อ", "/contact"],
];

const labels = {
  "/contract-drafting-ai": "AI ร่างสัญญา",
  "/contract-review-ai": "AI ตรวจสัญญา",
  "/thai-law-search": "ค้นกฎหมายไทย",
  "/letter-of-demand-ai": "AI หนังสือทวงถาม",
  "/nda-drafting-ai": "AI ร่าง NDA",
  "/privacy-policy-generator": "AI Privacy Policy",
  "/mcp": "KHORN MCP",
};

const GuidePage = () => {
  const { slug } = useParams();
  const guide = guides[slug];

  if (!guide) return <Navigate to="/" replace />;

  const url = `https://www.khorn-ai.com/guides/${slug}`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: guide.title,
        description: guide.description,
        inLanguage: "th",
        author: { "@type": "Organization", name: "KHORN AI" },
        publisher: { "@type": "Organization", name: "KHORN AI", url: "https://www.khorn-ai.com" },
        mainEntityOfPage: url,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "หน้าหลัก", item: "https://www.khorn-ai.com/" },
          { "@type": "ListItem", position: 2, name: "คู่มือ", item: "https://www.khorn-ai.com/guides" },
          { "@type": "ListItem", position: 3, name: guide.title, item: url },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen overflow-hidden bg-[#050505] text-white selection:bg-white selection:text-black">
      <Helmet>
        <title>{guide.title} | KHORN AI Guide</title>
        <meta name="description" content={guide.description} />
        <meta name="keywords" content={guide.keywords} />
        <meta property="og:title" content={`${guide.title} | KHORN AI Guide`} />
        <meta property="og:description" content={guide.description} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="article" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={url} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.035)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.035)_1px,transparent_1px)] bg-[size:72px_72px]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(255,255,255,0.12),transparent_32%)]" />
      </div>

      <header className="fixed top-0 z-50 w-full border-b border-white/[0.06] bg-black/35 backdrop-blur-2xl">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6">
          <Link to="/" className="flex items-center gap-4">
            <span className="text-3xl font-semibold leading-none tracking-tight text-white">K</span>
            <div>
              <div className="text-sm font-semibold tracking-tight text-white">KHORN AI</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/35">Thai Legal Intelligence</div>
            </div>
          </Link>
          <nav className="hidden items-center gap-8 lg:flex">
            {navItems.map(([label, href]) => (
              <a key={label} href={href} className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/45 transition hover:text-white">
                {label}
              </a>
            ))}
          </nav>
          <a href="https://app.khorn-ai.com/signup?plan=elite&commitment=monthly" className="rounded-full bg-white px-5 py-3 text-[11px] font-bold uppercase tracking-[0.18em] text-black transition hover:scale-[1.02]">
            เริ่มใช้งาน
          </a>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-5xl px-6 pb-24 pt-32">
        <article className="py-16">
          <div className="mb-6 flex items-center gap-3">
            <div className="h-px w-12 bg-white/20" />
            <span className="text-[10px] font-bold uppercase tracking-[0.35em] text-white/45">Legal guide</span>
          </div>
          <h1 className="text-5xl font-semibold leading-tight tracking-tight md:text-7xl">{guide.title}</h1>
          <p className="mt-8 max-w-3xl text-lg leading-relaxed text-white/55">{guide.intro}</p>

          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {guide.sections.map(([title, body]) => (
              <section key={title} className="rounded-[28px] border border-white/[0.07] bg-white/[0.04] p-7">
                <BookOpen size={20} className="mb-6 text-emerald-200" />
                <h2 className="text-2xl font-semibold">{title}</h2>
                <p className="mt-4 text-sm leading-relaxed text-white/52">{body}</p>
              </section>
            ))}
          </div>

          <section className="mt-8 rounded-[32px] border border-white/[0.07] bg-white/[0.04] p-8">
            <h2 className="text-3xl font-semibold">เช็กลิสต์เร็ว</h2>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {guide.checklist.map((item) => (
                <div key={item} className="flex items-center gap-3 rounded-2xl border border-white/[0.06] bg-black/25 px-4 py-3 text-sm text-white/70">
                  <CheckCircle2 size={16} className="text-emerald-200" />
                  {item}
                </div>
              ))}
            </div>
          </section>

          <section className="mt-8 rounded-[32px] border border-white/[0.07] bg-white/[0.04] p-8">
            <h2 className="text-3xl font-semibold">อ่านต่อและใช้เครื่องมือ</h2>
            <div className="mt-6 flex flex-wrap gap-3">
              {guide.related.map((href) => (
                <Link key={href} to={href} className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-bold text-black transition hover:scale-[1.02]">
                  {labels[href] || href}
                  <ArrowRight size={15} />
                </Link>
              ))}
            </div>
          </section>
        </article>
      </main>
    </div>
  );
};

export default GuidePage;
